<script setup>
import { computed } from 'vue';
import { useI18n } from 'vue-i18n';

// Linha embaixo do nome do agente: bolinha + status efetivo + "há X" compacto.
// `now` vem do pai (atualizado a cada minuto) para a duração andar junto com o polling.
const props = defineProps({
  status: { type: String, default: 'offline' },
  since: { type: String, default: null },
  now: { type: Number, required: true },
});

const { t } = useI18n();

const DOT_CLASSES = {
  online: 'bg-green-500',
  busy: 'bg-yellow-400',
  offline: 'bg-slate-400',
};

// "45s", "18min", "2h 10min" — cabe em oito colunas sem quebrar
const compactDuration = seconds => {
  if (seconds < 60) return `${seconds}s`;
  const minutes = Math.floor(seconds / 60);
  if (minutes < 60) return `${minutes}min`;
  const hours = Math.floor(minutes / 60);
  const rest = minutes % 60;
  return rest ? `${hours}h ${rest}min` : `${hours}h`;
};

const dotClass = computed(
  () => DOT_CLASSES[props.status] || DOT_CLASSES.offline
);

const label = computed(() => {
  if (props.status === 'online') return t('SUMMARY_REPORTS.STATUS.ONLINE');
  if (props.status === 'busy') return t('SUMMARY_REPORTS.STATUS.BUSY');
  return t('SUMMARY_REPORTS.STATUS.OFFLINE');
});

const sinceText = computed(() => {
  if (!props.since) return '';
  const elapsed = Math.max(
    0,
    Math.floor((props.now - new Date(props.since).getTime()) / 1000)
  );
  return t('SUMMARY_REPORTS.STATUS.SINCE', {
    duration: compactDuration(elapsed),
  });
});
</script>

<template>
  <div class="flex flex-wrap items-center gap-x-1.5 text-xs text-n-slate-11">
    <span class="inline-block w-2 h-2 rounded-full" :class="dotClass" />
    <span class="whitespace-nowrap">{{ label }}</span>
    <template v-if="sinceText">
      <span class="w-px h-3 bg-n-slate-6" />
      <span class="whitespace-nowrap">{{ sinceText }}</span>
    </template>
  </div>
</template>
